/*
 * Row labels, Home and the rows under the section titles alike, follow
 * `design/40-shell-and-pages.md` "Sidebar": `md` (14px) medium from 1280px and in the phone
 * drawer, `sm` (12px) from 768px to 1279px. The rail is a fixed 256–296px, so on a smaller window
 * the extra 2px costs a chat title more characters than it gains in legibility; the drawer below
 * 768px is wide enough not to need the trade.
 *
 * One property, set here and read by the rule below, so the widths live in one place. They are a
 * container query on the shell frame, which is the viewport-wide container the frame's own rail
 * widths already step on (`unified-shell-frame.module.css`), over the same range: the desktop rail
 * shows above `md` (48rem) and widens at `lg`. The phone drawer is portalled out of the frame, so
 * no container matches it and it keeps the 14px default; if it were ever mounted inside the frame,
 * the lower bound would still hold it at 14px below 48rem.
 *
 * The rule has to be this specific: Primer sets any label inside a `NavList.SubNav` to its small
 * body size with a two-class selector, which outranks `SubNavItemWrapper`'s font size on the
 * `<li>`. The section titles are not affected, as their own element sets 12px. Scoped to this
 * rail: the DMA rails mount the same rows and are not part of this change. `[id$='--label']` is
 * the label cell Primer renders; its class carries a build hash.
 */
.rail {
  --unified-rail-row-font-size: var(--font-size__md);
}

@container (48rem < width < 80rem) {
  .rail {
    --unified-rail-row-font-size: var(--font-size__sm);
  }
}

.rail li [id$='--label'] {
  font-size: var(--unified-rail-row-font-size);
}

/* The locked Custom companies row draws its label in its own button, which sets 14px itself. */
.rail li [id$='--label'] button {
  font-size: inherit;
}

/*
 * The delete control on a conversation row in the rail's Chats group: Primer's trailing action,
 * hidden at rest and revealed by the row's hover or by keyboard focus anywhere inside the row,
 * which is how the `/chat` history sidebar reveals its own (`chat/history/chat-history-sidebar.css`).
 * Always visible where there is no hover to reveal it.
 *
 * Laid over the row's trailing end rather than beside it. As a flex sibling it took its 32px out
 * of the link, so the active conversation's lilac pill stopped 32px short of every other active
 * row. The `<li>` is Primer's positioned item, so the control is placed against the full row, and
 * the link keeps the whole width and reserves the control's width as end padding so a long title
 * ellipsises before the glyph.
 */
.chatDelete {
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  /* Above the link it overlays, which Primer's item content gives a z-index of 1. */
  z-index: 2;
  display: flex;
  align-items: center;
  opacity: 0;
}

:global(li):hover > .chatDelete,
:global(li):focus-within > .chatDelete {
  opacity: 1;
}

/*
 * Match the history sidebar's 14px delete glyph while retaining Primer's 32px hit target.
 * The Core Line asset supplies the same stroke treatment as the leading navigation icons.
 */
.chatDelete svg {
  inline-size: 14px;
  block-size: 14px;
  /*
   * Centred in the 32px-wide button, the glyph sits 2px closer to the row's end than to the
   * 36px row's top and bottom. Shift the ink by half that difference so all three visible gaps
   * match; the button, and so the hit target, stays where it is.
   */
  translate: -2px 0;
}

/* Keep the Core Line asset's 1.5px stroke when shrinking its 20px view box. */
.chatDelete svg path {
  vector-effect: non-scaling-stroke;
}

/*
 * `2xl` (32px) is the trailing action's own width, so the label ends where the button begins.
 * Prefixed with `.rail` to outrank the shared row box's padding, which styled-components injects
 * after this module.
 */
.rail :global(li):has(> .chatDelete) > a {
  flex: 1;
  min-inline-size: 0;
  inline-size: auto;
  padding-inline-end: var(--space__2xl);
}

@media (hover: none) {
  .chatDelete {
    opacity: 1;
  }
}
